/**
 * Province time zones for meetings. The CRM's own rules, the same as the database (migration 76), so a phone or
 * browser with older time zone files can't put a meeting at the wrong hour. From Nov 1, 2026:
 * clocks change (2nd Sunday of March → 1st Sunday of November, 2 AM local): ON, QC, NB, NS, PE, NL;
 * no clock changes: MB (UTC−5), SK (UTC−6), AB (UTC−6), BC (UTC−7), YT (UTC−7).
 */
export type Zone = "ON" | "QC" | "NB" | "NS" | "PE" | "NL" | "MB" | "SK" | "AB" | "BC" | "YT";
const RULE: Record<Zone, { std: number; dst: boolean; name: string }> = {
  ON: { std: -300, dst: true, name: "Ontario" }, QC: { std: -300, dst: true, name: "Quebec" },
  NB: { std: -240, dst: true, name: "New Brunswick" }, NS: { std: -240, dst: true, name: "Nova Scotia" }, PE: { std: -240, dst: true, name: "PEI" },
  NL: { std: -210, dst: true, name: "Newfoundland" }, MB: { std: -300, dst: false, name: "Manitoba" },
  SK: { std: -360, dst: false, name: "Saskatchewan" }, AB: { std: -360, dst: false, name: "Alberta" },
  BC: { std: -420, dst: false, name: "BC" }, YT: { std: -420, dst: false, name: "Yukon" },
};
export const ZONES = Object.keys(RULE) as Zone[];
export const zoneName = (z?: string | null) => RULE[(z as Zone) ?? "ON"]?.name ?? "Ontario";
export const zoneLabel = (z?: string | null) => `${zoneName(z)} time`;
export const zoneChanges = (z: Zone) => RULE[z].dst;

const sunday = (y: number, m: number, from: number) => { const d = new Date(Date.UTC(y, m, from)); return from + ((7 - d.getUTCDay()) % 7); };
/** offset from UTC in minutes for a moment (ms) */
export function zoneOffset(ms: number, z: Zone = "ON"): number {
  const r = RULE[z] ?? RULE.ON;
  if (!r.dst) return r.std;
  const y = new Date(ms + r.std * 60000).getUTCFullYear();
  const s = Date.UTC(y, 2, sunday(y, 2, 8), 2) - r.std * 60000;
  const e = Date.UTC(y, 10, sunday(y, 10, 1), 2) - (r.std + 60) * 60000;
  return ms >= s && ms < e ? r.std + 60 : r.std;
}
const pad = (n: number) => String(n).padStart(2, "0");
/** a moment → "YYYY-MM-DDTHH:mm" on that province's clock */
export function zoneLocal(iso: string | number | Date, z: Zone = "ON"): string {
  const ms = new Date(iso).getTime(); const d = new Date(ms + zoneOffset(ms, z) * 60000);
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}T${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
}
/** "YYYY-MM-DDTHH:mm" on a province's clock → ISO moment */
export function zoneToUtc(local: string, z: Zone = "ON"): string {
  const [d, t = "00:00"] = local.split("T"); const [y, mo, da] = d.split("-").map(Number); const [h, mi] = t.split(":").map(Number);
  const wall = Date.UTC(y, mo - 1, da, h, mi);
  const guess = wall - RULE[z].std * 60000;
  return new Date(wall - zoneOffset(guess - 3600000, z) * 60000).toISOString();
}
/** "10:00 AM" from "YYYY-MM-DDTHH:mm" */
export const clock = (local: string) => { const [h, m] = local.slice(11, 16).split(":").map(Number); return `${((h + 11) % 12) + 1}:${pad(m)} ${h < 12 ? "AM" : "PM"}`; };
/** dropdown options; the hint compares with Ontario on the chosen date */
export function zoneOptions(atIso?: string) {
  const ms = atIso ? new Date(atIso).getTime() : Date.now();
  return ZONES.map((z) => {
    const diff = (zoneOffset(ms, z) - zoneOffset(ms, "ON")) / 60;
    const rel = diff === 0 ? "same as Ontario" : `${Math.abs(diff)} ${Math.abs(diff) === 1 ? "hour" : "hours"} ${diff > 0 ? "ahead of" : "behind"} Ontario`;
    return { value: z, label: `${RULE[z].name} · ${rel}${RULE[z].dst ? "" : " (no clock changes)"}` };
  });
}
